import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiFilter, FiX, FiTrash2, FiEye, FiRefreshCw, FiArrowRight, FiPlus, FiCalendar, FiClock, FiUser, FiActivity, FiCheck, FiCheckCircle, FiRepeat } from 'react-icons/fi';
import { getAllDoctors, updateDoctor, getDoctorsByMultipleGuilds } from '../doctors/doctorService';
import { getAllGuilds } from '../guilds/guildService';
import { getAllDepartments, getSpecialtiesByDepartment } from '../specialties/taxonomyService';
import { updateReferral, getAllReferrals, deleteReferral, createReferral, getReferralsByMultipleGuilds } from './referralService';
import { STATUS_PENDING, STATUS_ACCEPTED, STATUS_COMPLETED, STATUS_CANCELLED, STATUS_REJECTED, STATUS_RESCHEDULED } from '../shared/constants';
import './ReferralList.css';
import { Timestamp, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { useLocation } from 'react-router-dom';
import { triggerReferralNotifications } from '../notifications/notificationService';

/**
 * Maps a referral status string to the appropriate CSS badge class.
 * Classes are defined in ReferralList.css.
 */
function getStatusBadgeClass(status) {
    const base = 'status-badge';
    switch ((status || '').toLowerCase()) {
        case 'completed': return `${base} status-completed`;
        case 'pending': return `${base} status-pending`;
        case 'rescheduled': return `${base} status-rescheduled`;
        case 'cancelled':
        case 'rejected': return `${base} status-inactive`;
        case 'accepted': return `${base} status-active`;
        default: return base;
    }
}

/** Format a JS Date or Firestore Timestamp as DD/MM/YYYY */
function formatDate(value) {
    if (!value) return 'N/A';
    const d = value?.toDate ? value.toDate() : new Date(value);
    if (isNaN(d)) return 'N/A';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

/** Format a JS Date or Firestore Timestamp as DD/MM/YYYY HH:MM */
function formatDateTime(value) {
    if (!value) return 'N/A';
    const d = value?.toDate ? value.toDate() : new Date(value);
    if (isNaN(d)) return 'N/A';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

/** Resolve the display name of whoever created a referral */
function resolveCreatorName(referral, fromDoctor) {
    if (referral.fromDoctorId === 'assist_health') {
        const role = (referral.createdByRole || 'admin').toLowerCase();
        if (role === 'coordinator') {
            return `${referral.createdByName || 'Unknown'} (Coordinator)`;
        }
        return 'Admin';
    }
    return fromDoctor ? `Dr. ${fromDoctor.fullName}` : `Dr. ${referral.fromDoctorName || '—'}`;
}

export default function ReferralList({ restrictedGuilds = null }) {
    const { currentUser, userRole, userName } = useAuth();
    const [referrals, setReferrals] = useState([]);
    const [filteredReferrals, setFilteredReferrals] = useState([]);
    const [guilds, setGuilds] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, active: 0 });
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [rescheduleData, setRescheduleData] = useState({ date: '', time: '' });

    // New Referral State
    const [newReferral, setNewReferral] = useState({
        toDoctorId: '',
        toDepartmentId: '',
        toSpecialtyId: '',
        patientName: '',
        patientPhone: '',
        reason: '',
        clinicalNotes: '',
        appointmentDate: '',
        appointmentTime: ''
    });

    // Filter states
    const [filters, setFilters] = useState({
        guildId: '',
        status: '',
        search: '',
    });

    const [departments, setDepartments] = useState([]);
    const [createSpecialties, setCreateSpecialties] = useState([]);
    const [availableDoctors, setAvailableDoctors] = useState([]);
    const location = useLocation();
    const prefilledHandled = useRef(false);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        applyFilters();
        calculateStats();
    }, [referrals, filters]);

    async function loadData() {
        try {
            setLoading(true);
            let referralsData, doctorsData;
            const guildsData = await getAllGuilds();

            if (restrictedGuilds && restrictedGuilds.length > 0) {
                [referralsData, doctorsData] = await Promise.all([
                    getReferralsByMultipleGuilds(restrictedGuilds),
                    getDoctorsByMultipleGuilds(restrictedGuilds)
                ]);
            } else {
                [referralsData, doctorsData] = await Promise.all([
                    getAllReferrals(),
                    getAllDoctors()
                ]);
            }

            setReferrals(referralsData);
            setGuilds(guildsData);
            setDoctors(doctorsData);

            const depts = await getAllDepartments();
            setDepartments(depts);
            setError('');
        } catch (err) {
            setError('Failed to load data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    // Handle prefilledDoctor from URL query param (when navigating from Doctors page)
    useEffect(() => {
        if (prefilledHandled.current || loading || doctors.length === 0) return;
        const params = new URLSearchParams(location.search);
        const prefilledDoctorId = params.get('prefilledDoctor');
        if (prefilledDoctorId) {
            const doc = doctors.find(d => d.id === prefilledDoctorId || d.uid === prefilledDoctorId);
            if (doc) {
                prefilledHandled.current = true;
                // Pre-fill the referral form
                setNewReferral(prev => ({
                    ...prev,
                    toDoctorId: doc.id || doc.uid,
                    toDepartmentId: doc.departmentId || '',
                    toSpecialtyId: doc.specialtyId || '',
                }));
                // Load specialties for the department and set available doctors
                if (doc.departmentId) {
                    getSpecialtiesByDepartment(doc.departmentId).then(specs => {
                        setCreateSpecialties([...specs].sort((a, b) => a.name.localeCompare(b.name)));
                    });
                }
                if (doc.specialtyId) {
                    const docs = doctors
                        .filter(d => d.specialtyId === doc.specialtyId)
                        .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
                    setAvailableDoctors(docs);
                }
                setShowCreateModal(true);
            }
        }
    }, [loading, doctors, location.search]);

    function calculateStats() {
        const total = referrals.length;
        const pending = referrals.filter(r => r.status === STATUS_PENDING).length;
        const completed = referrals.filter(r => r.status === STATUS_COMPLETED).length;
        const active = referrals.filter(r => r.status === STATUS_ACCEPTED || r.status === STATUS_RESCHEDULED).length;
        setStats({ total, pending, completed, active });
    }

    function applyFilters() {
        let filtered = [...referrals];

        // Filter by guild
        if (filters.guildId) {
            filtered = filtered.filter(r =>
                r.fromGuildId === filters.guildId ||
                r.toGuildId === filters.guildId
            );
        }

        // Filter by status
        if (filters.status) {
            filtered = filtered.filter(r => r.status === filters.status);
        }

        // Filter by search (patientName, doctorName, or serial number)
        if (filters.search) {
            const searchLower = filters.search.toLowerCase().replace(/^#/, '');
            const searchNum = parseInt(searchLower, 10);
            filtered = filtered.filter(r =>
                (r.patientName?.toLowerCase().includes(searchLower)) ||
                (r.fromDoctorName?.toLowerCase().includes(searchLower)) ||
                (r.toDoctorName?.toLowerCase().includes(searchLower)) ||
                (!isNaN(searchNum) && r.serialNumber === searchNum) ||
                (r.serialNumber && String(r.serialNumber).padStart(3, '0').includes(searchLower))
            );
        }

        setFilteredReferrals(filtered);
    }

    function handleFilterChange(e) {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }

    function clearFilters() {
        setFilters({
            guildId: '',
            status: '',
            search: '',
        });
    }

    async function handleDelete(id) {
        if (window.confirm('Are you sure you want to delete this referral record?')) {
            try {
                await deleteReferral(id);
                setReferrals(referrals.filter(r => r.id !== id));
                if (selectedReferral && selectedReferral.id === id) {
                    setSelectedReferral(null);
                }
            } catch (err) {
                alert('Failed to delete referral');
            }
        }
    }

    async function handleCDepartmentChange(deptId) {
        setNewReferral(prev => ({ ...prev, toDepartmentId: deptId, toSpecialtyId: '', toDoctorId: '' }));
        setAvailableDoctors([]);

        if (deptId) {
            try {
                const specs = await getSpecialtiesByDepartment(deptId);
                // Sort specialties A-Z
                setCreateSpecialties([...specs].sort((a, b) => a.name.localeCompare(b.name)));
            } catch (err) {
                console.error('Error loading specialties:', err);
            }
        } else {
            setCreateSpecialties([]);
        }
    }

    function handleCSpecialtyChange(specId) {
        setNewReferral(prev => ({ ...prev, toSpecialtyId: specId, toDoctorId: '' }));

        if (specId) {
            // Sort doctors A-Z
            const docs = doctors
                .filter(d => d.specialtyId === specId)
                .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
            setAvailableDoctors(docs);
        } else {
            setAvailableDoctors([]);
        }
    }

    async function handleCreateReferral(e) {
        e.preventDefault();
        try {
            const targetDoctor = doctors.find(d => d.id === newReferral.toDoctorId || d.uid === newReferral.toDoctorId);
            if (!targetDoctor) {
                alert('Please select a target doctor');
                return;
            }

            // Validate date and time against current time
            if (newReferral.appointmentDate && newReferral.appointmentTime) {
                const selectedDateTime = new Date(`${newReferral.appointmentDate}T${newReferral.appointmentTime}`);
                if (selectedDateTime < new Date()) {
                    alert('Cannot schedule an appointment for a past time.');
                    return;
                }
            }

            const appointmentDateTime = (newReferral.appointmentDate && newReferral.appointmentTime)
                ? new Date(`${newReferral.appointmentDate}T${newReferral.appointmentTime}`)
                : (newReferral.appointmentDate ? new Date(newReferral.appointmentDate) : null);

            const referralData = {
                ...newReferral,
                fromDoctorId: 'assist_health',
                fromDoctorName: 'AssistHealth',
                fromGuildId: 'system', // Special guild ID for AssistHealth
                toDoctorName: targetDoctor.fullName || targetDoctor.name,
                toGuildId: targetDoctor.guildId,
                isCrossGuild: 'system' !== targetDoctor.guildId,
                status: STATUS_PENDING,
                appointmentDate: appointmentDateTime ? Timestamp.fromDate(appointmentDateTime) : null,
                // Track who actually created this referral (admin or coordinator)
                createdByName: userName || currentUser?.displayName || currentUser?.email || 'Admin',
                createdByRole: userRole || 'admin',
                createdById: currentUser?.uid || '',
            };


            const createdId = await createReferral(referralData);

            // Trigger notifications
            await triggerReferralNotifications({ id: createdId, ...referralData }, 'new_referral', {
                senderId: currentUser.uid,
                senderName: 'Admin System',
                newStatus: STATUS_PENDING
            });

            setShowCreateModal(false);
            setNewReferral({
                toDoctorId: '',
                toDepartmentId: '',
                toSpecialtyId: '',
                patientName: '',
                patientPhone: '',
                reason: '',
                clinicalNotes: '',
                appointmentDate: '',
                appointmentTime: ''
            });
            loadData(); // Reload list
        } catch (err) {
            alert('Failed to create referral');
            console.error(err);
        }
    }

    async function handleUpdateStatus(id, newStatus, additionalData = {}, skipConfirm = false) {
        if (!skipConfirm) {
            const labels = {
                accepted: 'accept this referral',
                completed: 'mark this referral as completed',
                rejected: 'reject this referral',
                cancelled: 'cancel this referral',
                rescheduled: 'reschedule this appointment',
            };
            const msg = labels[newStatus] || `change status to "${newStatus}"`;
            if (!window.confirm(`Are you sure you want to ${msg}? This action cannot be undone.`)) return;
        }

        try {
            await updateReferral(id, {
                status: newStatus,
                ...additionalData
            });

            // Trigger notifications
            const referral = referrals.find(r => r.id === id);
            if (referral) {
                await triggerReferralNotifications(referral, 'status_change', {
                    senderId: currentUser.uid,
                    senderName: 'Admin/Coordinator',
                    newStatus: newStatus
                });
            }

            loadData();
            if (showRescheduleModal) setShowRescheduleModal(false);
            if (selectedReferral) setSelectedReferral(null);
        } catch (err) {
            alert(`Failed to update status to ${newStatus}`);
            console.error(err);
        }
    }

    async function handleRescheduleSubmit(e) {
        e.preventDefault();
        if (!rescheduleData.date || !rescheduleData.time) {
            alert('Please select both date and time');
            return;
        }

        const dateTime = new Date(`${rescheduleData.date}T${rescheduleData.time}`);
        await handleUpdateStatus(selectedReferral.id, STATUS_RESCHEDULED, {
            appointmentDate: Timestamp.fromDate(dateTime),
            rescheduledAt: serverTimestamp()
        });
    }

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case STATUS_ACCEPTED: return 'status-badge status-active';
            case STATUS_PENDING: return 'status-badge status-pending';
            case STATUS_COMPLETED: return 'status-badge status-completed';
            case STATUS_CANCELLED:
            case STATUS_REJECTED: return 'status-badge status-inactive';
            case STATUS_RESCHEDULED: return 'status-badge status-rescheduled';
            default: return 'status-badge';
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <FiRefreshCw className="spin-icon" />
                <p>Loading referrals...</p>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h1>Referral Network</h1>
                    <p className="subtitle">Managing patient journeys under AssistHealth</p>
                </div>
                <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                    <FiPlus /> New Referral
                </button>
            </div>

            {/* Summary Cards */}
            <div className="referral-summary">
                <div className="summary-card">
                    <span className="label">Total Referrals</span>
                    <span className="value">{stats.total}</span>
                </div>
                <div className="summary-card">
                    <span className="label">Decision Pending</span>
                    <span className="value" style={{ color: 'var(--pending-orange)' }}>{stats.pending}</span>
                </div>
                <div className="summary-card">
                    <span className="label">Active/Rescheduled</span>
                    <span className="value" style={{ color: 'var(--primary-blue)' }}>{stats.active}</span>
                </div>
                <div className="summary-card">
                    <span className="label">Successfully Completed</span>
                    <span className="value" style={{ color: 'var(--success-green)' }}>{stats.completed}</span>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="filters-section">
                <div className="search-box">
                    <FiSearch />
                    <input
                        type="text"
                        name="search"
                        placeholder="Search patient or doctor..."
                        value={filters.search}
                        onChange={handleFilterChange}
                    />
                </div>

                <div className="filter-group">
                    <FiFilter />
                    <select
                        name="guildId"
                        value={filters.guildId}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Guilds</option>
                        {guilds.map(guild => (
                            <option key={guild.id} value={guild.id}>{guild.name}</option>
                        ))}
                    </select>

                    <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Statuses</option>
                        <option value={STATUS_PENDING}>Pending</option>
                        <option value={STATUS_ACCEPTED}>Accepted</option>
                        <option value={STATUS_RESCHEDULED}>Rescheduled</option>
                        <option value={STATUS_COMPLETED}>Completed</option>
                        <option value={STATUS_REJECTED}>Rejected</option>
                        <option value={STATUS_CANCELLED}>Cancelled</option>
                    </select>

                    {(filters.guildId || filters.status || filters.search) && (
                        <button className="clear-filters" onClick={clearFilters}>
                            <FiX /> Clear
                        </button>
                    )}
                </div>
            </div>

            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Patient Name</th>
                            <th>Phone</th>
                            <th>From Doctor</th>
                            <th>From Guild</th>
                            <th>To Doctor</th>
                            <th>To Guild</th>
                            <th>Status</th>
                            <th>Created At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredReferrals.length > 0 ? (
                            filteredReferrals.map((referral) => {
                                const fromDoctor = doctors.find(d => d.id === referral.fromDoctorId || d.uid === referral.fromDoctorId);
                                const toDoctor = doctors.find(d => d.id === referral.toDoctorId || d.uid === referral.toDoctorId);

                                return (
                                    <tr
                                        key={referral.id}
                                        className="clickable-row"
                                        onClick={() => setSelectedReferral(referral)}
                                    >
                                        <td><span style={{ fontWeight: 600, color: '#64748b' }}>{referral.serialNumber ? `#${String(referral.serialNumber).padStart(3, '0')}` : '—'}</span></td>
                                        <td>
                                            <div className="patient-info">
                                                <span className="patient-name">{referral.patientName}</span>
                                            </div>
                                        </td>
                                        <td><span className="phone-text">{referral.patientPhone}</span></td>
                                        <td>
                                            <div className="doctor-cell">
                                                <span className="doctor-name">
                                                    {resolveCreatorName(referral, fromDoctor)}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="guild-tag-mini">
                                                {(() => {
                                                    const fromGuildId = (fromDoctor && referral.fromDoctorId !== 'assist_health') ? fromDoctor.guildId : referral.fromGuildId;
                                                    return guilds.find(g => g.id === fromGuildId)?.name || 'System';
                                                })()}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="doctor-cell">
                                                <span className="doctor-name">
                                                    {toDoctor ? `Dr. ${toDoctor.fullName}` : `Dr. ${referral.toDoctorName}`}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="guild-tag-mini">
                                                {(() => {
                                                    const toGuildId = toDoctor ? toDoctor.guildId : referral.toGuildId;
                                                    return guilds.find(g => g.id === toGuildId)?.name || 'Unknown';
                                                })()}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={getStatusBadgeClass(referral.status)}>
                                                {referral.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(referral.createdAt)}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="9" className="empty-row">No referrals found matching your criteria</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {selectedReferral && (
                <div className="modal-overlay" onClick={() => setSelectedReferral(null)}>
                    <div className="modal-content detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="detail-modal-header">
                            <div className="header-badge">
                                <span className={getStatusBadgeClass(selectedReferral.status)}>{selectedReferral.status}</span>
                            </div>
                            <h2>Referral Overview</h2>
                            <button className="close-btn" onClick={() => setSelectedReferral(null)}><FiX /></button>
                        </div>

                        <div className="detail-modal-grid">
                            <div className="detail-main">
                                <section className="detail-card">
                                    <div className="section-title">
                                        <FiUser /> <h3>Patient Information</h3>
                                    </div>
                                    <div className="info-row">
                                        <div className="info-block">
                                            <label>Full Name</label>
                                            <p>{selectedReferral.patientName}</p>
                                        </div>
                                        <div className="info-block">
                                            <label>Contact Number</label>
                                            <p>{selectedReferral.patientPhone}</p>
                                        </div>
                                    </div>
                                </section>

                                <section className="detail-card">
                                    <div className="section-title">
                                        <FiActivity /> <h3>Clinical Details</h3>
                                    </div>
                                    <div className="info-block full">
                                        <label>Appointment Schedule</label>
                                        <div className="content-box">
                                            {selectedReferral.appointmentDate?.toDate ? (
                                                <span className="datetime">
                                                    <FiCalendar /> {formatDate(selectedReferral.appointmentDate)}
                                                    <FiClock style={{ marginLeft: 12 }} /> {new Date(selectedReferral.appointmentDate.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                                                </span>
                                            ) : (
                                                'Appointment not yet scheduled'
                                            )}
                                        </div>
                                    </div>

                                    <div className="info-block full">
                                        <label>Reason for Referral</label>
                                        <div className="content-box">{selectedReferral.reason}</div>
                                    </div>

                                    <div className="info-block full">
                                        <label>Clinical Notes</label>
                                        <div className="content-box notes">
                                            {selectedReferral.clinicalNotes || 'No clinical notes provided'}
                                        </div>
                                    </div>

                                    <div className="info-block">
                                        <label>Created At</label>
                                        <p>{formatDateTime(selectedReferral.createdAt)}</p>
                                    </div>
                                </section>
                            </div>

                            <div className="detail-sidebar">
                                <section className="detail-card journey-card">
                                    <div className="section-title">
                                        <FiRepeat /> <h3>Journey Path</h3>
                                    </div>
                                    <div className="path-visualization">
                                        {(() => {
                                            const fromDoctor = doctors.find(d => d.id === selectedReferral.fromDoctorId || d.uid === selectedReferral.fromDoctorId);
                                            const toDoctor = doctors.find(d => d.id === selectedReferral.toDoctorId || d.uid === selectedReferral.toDoctorId);

                                            return (
                                                <>
                                                    <div className="path-step">
                                                        <div className="step-marker from"></div>
                                                        <div className="step-info">
                                                            <label>Source</label>
                                                            <p>
                                                                {resolveCreatorName(selectedReferral, doctors.find(d => d.id === selectedReferral.fromDoctorId || d.uid === selectedReferral.fromDoctorId))}
                                                            </p>
                                                            <span>
                                                                {(() => {
                                                                    const fromGuildId = (fromDoctor && selectedReferral.fromDoctorId !== 'assist_health') ? fromDoctor.guildId : selectedReferral.fromGuildId;
                                                                    return guilds.find(g => g.id === fromGuildId)?.name || 'Official System';
                                                                })()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="path-line"></div>
                                                    <div className="path-step">
                                                        <div className="step-marker to"></div>
                                                        <div className="step-info">
                                                            <label>Destination</label>
                                                            <p>
                                                                {toDoctor ? `Dr. ${toDoctor.fullName}` : `Dr. ${selectedReferral.toDoctorName}`}
                                                            </p>
                                                            <span>
                                                                {(() => {
                                                                    const toGuildId = toDoctor ? toDoctor.guildId : selectedReferral.toGuildId;
                                                                    return guilds.find(g => g.id === toGuildId)?.name || 'Unknown';
                                                                })()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </section>

                                <div className="detail-footer-actions">
                                    <div className="footer-label">Management Actions</div>
                                    <div className="action-button-group horizontal">
                                        {selectedReferral.status === STATUS_PENDING && (
                                            <button
                                                className="icon-btn accept"
                                                onClick={() => handleUpdateStatus(selectedReferral.id, STATUS_ACCEPTED)}
                                            >
                                                <FiCheck /> Accept
                                            </button>
                                        )}
                                        {(selectedReferral.status === STATUS_PENDING || selectedReferral.status === STATUS_ACCEPTED || selectedReferral.status === STATUS_RESCHEDULED) && (
                                            <>
                                                <button
                                                    className="icon-btn reschedule"
                                                    onClick={() => {
                                                        if (window.confirm('Open the reschedule picker?')) setShowRescheduleModal(true);
                                                    }}
                                                >
                                                    <FiClock /> Reschedule
                                                </button>
                                                <button
                                                    className="icon-btn complete"
                                                    onClick={() => handleUpdateStatus(selectedReferral.id, STATUS_COMPLETED)}
                                                >
                                                    <FiCheckCircle /> Complete
                                                </button>
                                                <button
                                                    className="icon-btn reject"
                                                    onClick={() => handleUpdateStatus(selectedReferral.id, STATUS_REJECTED)}
                                                >
                                                    <FiX /> Reject
                                                </button>
                                                <button
                                                    className="icon-btn cancel-btn"
                                                    onClick={() => handleUpdateStatus(selectedReferral.id, STATUS_CANCELLED)}
                                                >
                                                    <FiX /> Cancel
                                                </button>
                                            </>
                                        )}
                                        <button
                                            className="icon-btn delete"
                                            onClick={() => handleDelete(selectedReferral.id)}
                                        >
                                            <FiTrash2 /> Delete Record
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* Create Referral Modal */}
            {
                showCreateModal && (
                    <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Create New Referral</h2>
                                <button className="close-modal" onClick={() => setShowCreateModal(false)}><FiX /></button>
                            </div>
                            <form onSubmit={handleCreateReferral}>
                                <div className="create-referral-form">
                                    <div className="form-group full-width">
                                        <label>Referring As</label>
                                        <div className="doctor-info-cell">
                                            <span className="assist-health-label">AssistHealth</span>
                                            <span className="doctor-name">Direct Referral</span>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Target Department</label>
                                        <select
                                            required
                                            value={newReferral.toDepartmentId}
                                            onChange={e => handleCDepartmentChange(e.target.value)}
                                        >
                                            <option value="">Select Department</option>
                                            {[...departments].sort((a, b) => a.name.localeCompare(b.name)).map(dept => (
                                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Target Specialty</label>
                                        <select
                                            required
                                            disabled={!newReferral.toDepartmentId}
                                            value={newReferral.toSpecialtyId}
                                            onChange={e => handleCSpecialtyChange(e.target.value)}
                                        >
                                            <option value="">Select Specialty</option>
                                            {createSpecialties.map(spec => (
                                                <option key={spec.id} value={spec.id}>{spec.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Target Doctor</label>
                                        <select
                                            required
                                            disabled={!newReferral.toSpecialtyId}
                                            value={newReferral.toDoctorId}
                                            onChange={e => setNewReferral({ ...newReferral, toDoctorId: e.target.value })}
                                        >
                                            <option value="">Choose a doctor...</option>
                                            {availableDoctors.map(doc => (
                                                <option key={doc.id || doc.uid} value={doc.id || doc.uid}>
                                                    {doc.fullName} - {guilds.find(g => g.id === doc.guildId)?.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Appointment Date</label>
                                            <input
                                                type="date"
                                                required
                                                value={newReferral.appointmentDate}
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={e => setNewReferral({ ...newReferral, appointmentDate: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Appointment Time</label>
                                            <input
                                                type="time"
                                                required
                                                value={newReferral.appointmentTime}
                                                onChange={e => setNewReferral({ ...newReferral, appointmentTime: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Patient Full Name</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Enter patient name"
                                            value={newReferral.patientName}
                                            onChange={e => setNewReferral({ ...newReferral, patientName: e.target.value })}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Patient Contact Number</label>
                                        <input
                                            type="tel"
                                            required
                                            placeholder="+91 XXXXX XXXXX"
                                            value={newReferral.patientPhone}
                                            onChange={e => setNewReferral({ ...newReferral, patientPhone: e.target.value })}
                                        />
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Reason for Referral</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. Second opinion on cardio-thoracic report"
                                            value={newReferral.reason}
                                            onChange={e => setNewReferral({ ...newReferral, reason: e.target.value })}
                                        />
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Clinical Notes (Optional)</label>
                                        <textarea
                                            rows="3"
                                            placeholder="Additional history or clinical observations..."
                                            value={newReferral.clinicalNotes}
                                            onChange={e => setNewReferral({ ...newReferral, clinicalNotes: e.target.value })}
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                                    <button type="submit" className="btn-primary">Create Referral</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
            {/* Reschedule Modal */}
            {
                showRescheduleModal && (
                    <div className="modal-overlay" onClick={() => setShowRescheduleModal(false)}>
                        <div className="modal-content small-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Reschedule Appointment</h2>
                                <button className="close-modal" onClick={() => setShowRescheduleModal(false)}><FiX /></button>
                            </div>
                            <form onSubmit={handleRescheduleSubmit}>
                                <div className="modal-body">
                                    <p>Set a new date and time for <strong>{selectedReferral?.patientName}</strong>'s appointment.</p>
                                    <div className="form-group">
                                        <label>New Date</label>
                                        <input
                                            type="date"
                                            required
                                            value={rescheduleData.date}
                                            onChange={e => setRescheduleData({ ...rescheduleData, date: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>New Time</label>
                                        <input
                                            type="time"
                                            required
                                            value={rescheduleData.time}
                                            onChange={e => setRescheduleData({ ...rescheduleData, time: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn-secondary" onClick={() => setShowRescheduleModal(false)}>Cancel</button>
                                    <button type="submit" className="btn-primary">Update Schedule</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </div >
    );
}
